import React, { useEffect, useRef, useState } from 'react';
import { Check, X, ArrowRight, RefreshCw } from 'lucide-react';
import { comparisonData } from '../../data/mock';

const ComparisonSection = ({ onOpenCalendar }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="compare"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--brand-dark)' }}
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--brand-primary)' }} />
        <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--brand-primary)' }} />
      </div>

      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'rgba(211, 255, 98, 0.2)', color: 'var(--brand-primary)' }}
          >
            <RefreshCw size={16} />
            <span className="font-semibold text-sm">The Transformation</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'white' }}
          >
            {comparisonData.title}
          </h2>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {comparisonData.subtitle}
          </p>
        </div>

        {/* Comparison Table */}
        <div 
          className={`max-w-4xl mx-auto rounded-3xl overflow-hidden ${
            isVisible ? 'animate-scaleIn stagger-3' : 'opacity-0'
          }`}
          style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}
        >
          {/* Table Header */}
          <div className="grid grid-cols-3 gap-4 p-4 md:p-6" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
            <div className="body-medium font-semibold" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Aspect
            </div>
            <div className="text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#FF6B6B' }}
              >
                <X size={16} />
                <span className="font-medium text-sm">Before</span>
              </div>
            </div>
            <div className="text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
              >
                <Check size={16} />
                <span className="font-bold text-sm">After Zenova</span>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-white/10">
            {comparisonData.comparisons.map((row, index) => (
              <div 
                key={index}
                className="grid grid-cols-3 gap-4 p-4 md:p-6 transition-colors hover:bg-white/5"
              >
                <div className="body-small font-medium" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {row.aspect}
                </div>
                <div className="text-center">
                  <span 
                    className="body-small"
                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {row.before}
                  </span>
                </div>
                <div className="text-center">
                  <span 
                    className="inline-flex items-center gap-1 body-small font-semibold"
                    style={{ color: 'var(--brand-primary)' }}
                  >
                    {row.after}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-12 ${isVisible ? 'animate-fadeInUp stagger-5' : 'opacity-0'}`}>
          <button
            onClick={onOpenCalendar}
            className="btn-accent"
          >
            Start Your Transformation
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
